import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

/**
 * Demo component showcasing OKLCH color manipulation capabilities
 */
export const OKLCHDemo = () => {
	return (
		<div className="space-y-8">
			<Card className="p-6">
				<h2 className="text-2xl font-semibold mb-4">OKLCH Color Manipulation</h2>
				<p className="text-muted-foreground mb-6">
					With OKLCH, we can dynamically adjust colors while maintaining perceptual consistency.
				</p>
				
				{/* Dynamic hover states */}
				<div className="space-y-4">
					<h3 className="text-lg font-medium">Dynamic Hover States</h3>
					<div className="flex gap-4 flex-wrap">
						<Button 
							className="transition-all hover:brightness-110"
							style={{
								'--hover-bg': 'oklch(from var(--primary) calc(l + 0.1) c h)',
							} as React.CSSProperties}
						>
							Lighter on Hover
						</Button>
						<Button 
							className="transition-all"
							style={{
								backgroundColor: 'var(--primary)',
								'--hover-bg': 'oklch(from var(--primary) l calc(c * 1.3) h)',
							} as React.CSSProperties}
							onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--hover-bg)'}
							onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--primary)'}
						>
							More Vibrant on Hover
						</Button>
						<Button 
							variant="outline"
							className="transition-all"
							style={{
								'--hover-bg': 'oklch(from var(--primary) l c h / 0.1)',
							} as React.CSSProperties}
							onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--hover-bg)'}
							onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
						>
							Subtle Background on Hover
						</Button>
					</div>
				</div>

				{/* Color variations */}
				<div className="space-y-4 mt-8">
					<h3 className="text-lg font-medium">Automatic Color Variations</h3>
					<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
						<div className="space-y-2">
							<div 
								className="h-20 rounded-lg"
								style={{ backgroundColor: 'var(--primary)' }}
							/>
							<p className="text-sm">Original</p>
						</div>
						<div className="space-y-2">
							<div 
								className="h-20 rounded-lg"
								style={{ backgroundColor: 'oklch(from var(--primary) calc(l + 0.2) c h)' }}
							/>
							<p className="text-sm">20% Lighter</p>
						</div>
						<div className="space-y-2">
							<div 
								className="h-20 rounded-lg"
								style={{ backgroundColor: 'oklch(from var(--primary) calc(l - 0.2) c h)' }}
							/>
							<p className="text-sm">20% Darker</p>
						</div>
						<div className="space-y-2">
							<div 
								className="h-20 rounded-lg"
								style={{ backgroundColor: 'oklch(from var(--primary) l calc(c * 0.5) h)' }}
							/>
							<p className="text-sm">50% Desaturated</p>
						</div>
					</div>
				</div>

				{/* Complementary colors */}
				<div className="space-y-4 mt-8">
					<h3 className="text-lg font-medium">Color Harmony</h3>
					<div className="grid grid-cols-3 gap-4">
						<div className="space-y-2">
							<div 
								className="h-20 rounded-lg"
								style={{ backgroundColor: 'var(--primary)' }}
							/>
							<p className="text-sm">Primary</p>
						</div>
						<div className="space-y-2">
							<div 
								className="h-20 rounded-lg"
								style={{ backgroundColor: 'oklch(from var(--primary) l c calc(h + 120))' }}
							/>
							<p className="text-sm">Triadic (+120°)</p>
						</div>
						<div className="space-y-2">
							<div 
								className="h-20 rounded-lg"
								style={{ backgroundColor: 'oklch(from var(--primary) l c calc(h + 240))' }}
							/>
							<p className="text-sm">Triadic (+240°)</p>
						</div>
					</div>
				</div>

				{/* Opacity variations */}
				<div className="space-y-4 mt-8">
					<h3 className="text-lg font-medium">Opacity Variations</h3>
					<div className="flex gap-4 flex-wrap">
						{[1, 0.8, 0.6, 0.4, 0.2].map(opacity => (
							<div key={opacity} className="space-y-2">
								<div 
									className="h-16 w-16 rounded-lg border"
									style={{ backgroundColor: `oklch(from var(--primary) l c h / ${opacity})` }}
								/>
								<p className="text-xs text-center">{opacity * 100}%</p>
							</div>
						))}
					</div>
				</div>
			</Card>

			{/* Benefits explanation */}
			<Card className="p-6">
				<h3 className="text-lg font-medium mb-4">Benefits of OKLCH</h3>
				<ul className="space-y-2 text-sm text-muted-foreground">
					<li>• Perceptually uniform color adjustments</li>
					<li>• Easy creation of color variations and themes</li>
					<li>• Consistent hover/focus states across all colors</li>
					<li>• Automatic accessibility improvements</li>
					<li>• Future-proof with wide gamut display support</li>
				</ul>
			</Card>
		</div>
	)
}